import React, { useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $createTextNode, $createParagraphNode } from 'lexical';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import ToolbarPlugin from './ToolbarPlugin';

interface LexicalEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
  showToolbar?: boolean;
  autoFocus?: boolean;
}

// Create editor configuration with initial content
const createEditorConfig = (initialValue?: string) => ({
  namespace: 'EdgeLexEditor',
  editorState: initialValue ? () => {
    const root = $getRoot();
    if (root.isEmpty()) {
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(initialValue));
      root.append(paragraph);
    }
  } : undefined,
  theme: {
    root: 'outline-none',
    link: 'text-lex-accent hover:text-lex-accent-hover underline',
    text: {
      bold: 'font-bold',
      italic: 'italic',
      underline: 'underline',
      strikethrough: 'line-through',
      underlineStrikethrough: 'underline line-through',
    },
    list: {
      ul: 'list-disc list-inside',
      ol: 'list-decimal list-inside',
      listitem: 'ml-4',
    },
    code: 'bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono',
  },
  onError: (error: Error) => {
    console.error('Lexical Editor Error:', error);
  },
});

// Plugin for handling editor state changes
function OnChangePluginWrapper({ onChange }: { onChange?: (value: string) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        
        if (onChange) {
          // Convert editor state to text content
          const textContent = root.getTextContent();
          onChange(textContent);
        }
      });
    });
  }, [editor, onChange]);

  return null;
}

// Simple plugin to handle changes - no content updates to avoid conflicts

const LexicalEditorComponent: React.FC<LexicalEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  readOnly = false,
  className = '',
  minHeight = '100px',
  maxHeight = '400px',
  showToolbar = true,
  autoFocus = false,
}) => {
  return (
    <div className={`lexical-editor ${className}`}>
      <LexicalComposer initialConfig={createEditorConfig(value)}>
        {showToolbar && <ToolbarPlugin readOnly={readOnly} />}
        
        <div 
          className="relative border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
          style={{ minHeight, maxHeight }}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={`outline-none px-4 py-3 text-gray-900 dark:text-white ${
                  readOnly ? 'cursor-default' : 'cursor-text'
                }`}
                style={{ minHeight: showToolbar ? 'calc(100% - 40px)' : '100%' }}
                readOnly={readOnly}
                autoFocus={autoFocus}
              />
            }
            placeholder={
              <div className="absolute top-3 left-4 text-gray-400 dark:text-gray-500 pointer-events-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          
          <HistoryPlugin />
          <OnChangePluginWrapper onChange={onChange} />
        </div>
      </LexicalComposer>
    </div>
  );
};

export default LexicalEditorComponent; 